/**
 * FilterBar — reusable dashboard filter row.
 *
 * P1 scope: the time-filter control (preset dropdown + custom range). Presets
 * are fetched from /api/dashboard/v2/presets so the UI stays in lockstep with
 * the backend; a static fallback keeps it working if that call fails.
 *
 * Dimension filters (site/department/area/etc.) plug in via the `extra` slot
 * for now and become first-class controls in a later increment.
 */
import React, { useEffect, useState } from 'react';
import { fetchTimePresets } from '../api/client';

// Fallback mirrors services/dashboard/time_filters.PRESET_LABELS order.
const FALLBACK_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_7_days', label: 'Last 7 Days' },
  { id: 'last_30_days', label: 'Last 30 Days' },
  { id: 'current_month', label: 'Current Month' },
  { id: 'previous_month', label: 'Previous Month' },
  { id: 'quarter_to_date', label: 'Quarter to Date' },
  { id: 'half_yearly', label: 'Half Yearly' },
  { id: 'yearly', label: 'Yearly' },
  { id: 'financial_year', label: 'Financial Year' },
  { id: 'custom', label: 'Custom Date Range' },
];

const selectCls =
  'mt-1 block rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ' +
  'text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 ' +
  'focus:ring-blue-500';
const labelCls = 'text-xs font-medium text-gray-500 uppercase tracking-wide';

export default function FilterBar({
  preset,
  dateFrom,
  dateTo,
  onChange,
  extra = null,
  className = '',
}) {
  const [presets, setPresets] = useState(FALLBACK_PRESETS);

  useEffect(() => {
    let live = true;
    fetchTimePresets()
      .then((p) => {
        if (live && Array.isArray(p) && p.length) setPresets(p);
      })
      .catch(() => {
        /* keep fallback presets */
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className={`flex flex-wrap items-end gap-3 ${className}`}>
      <label className="flex flex-col">
        <span className={labelCls}>Period</span>
        <select
          className={selectCls}
          value={preset}
          onChange={(e) => onChange({ preset: e.target.value })}
        >
          {presets.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>

      {preset === 'custom' && (
        <>
          <label className="flex flex-col">
            <span className={labelCls}>From</span>
            <input
              type="date"
              className={selectCls}
              value={dateFrom || ''}
              max={dateTo || undefined}
              onChange={(e) =>
                onChange({ preset: 'custom', dateFrom: e.target.value, dateTo })
              }
            />
          </label>
          <label className="flex flex-col">
            <span className={labelCls}>To</span>
            <input
              type="date"
              className={selectCls}
              value={dateTo || ''}
              min={dateFrom || undefined}
              onChange={(e) =>
                onChange({ preset: 'custom', dateFrom, dateTo: e.target.value })
              }
            />
          </label>
        </>
      )}

      {extra}
    </div>
  );
}
